import React from 'react';

interface TypographyProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
  className?: string;
}

// Unified Typography System - Centralized Font Sizes
// All components use consistent font sizes and weights

// H1 - Main Page Titles (32px/2rem) - Web Standard
export const AppTitle: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <h1 className={`text-primary text-3xl font-bold font-zain leading-tight tracking-wide ${className}`} {...props}>
    {children}
  </h1>
);

// H2 - Section Headers (24px/1.5rem) - Web Standard
export const AppSubtitle: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <h2 className={`text-grayText text-2xl font-semibold font-zain leading-tight tracking-wide ${className}`} {...props}>
    {children}
  </h2>
);

// H3 - Card Titles (20px/1.25rem) - Web Standard
export const AppCardTitle: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <h3 className={`text-grayText text-xl font-semibold font-zain leading-tight ${className}`} {...props}>
    {children}
  </h3>
);

// H4 - Subsection Headers (18px/1.125rem) - Web Standard
export const AppSectionTitle: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <h4 className={`text-grayText text-lg font-semibold font-zain leading-tight ${className}`} {...props}>
    {children}
  </h4>
);

// Body - Main Content (16px/1rem) - Web Standard
export const AppText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <p className={`text-grayText text-base font-normal font-zain leading-relaxed ${className}`} {...props}>
    {children}
  </p>
);

// Small - Secondary Text (14px/0.875rem) - Web Standard
export const AppBodyText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <p className={`text-grayText text-sm font-normal font-zain leading-relaxed ${className}`} {...props}>
    {children}
  </p>
);

// Small - Secondary Text (12px/0.75rem) - Web Standard
export const AppSmallText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <small className={`text-grayUnit text-xs font-normal font-zain leading-relaxed ${className}`} {...props}>
    {children}
  </small>
);

// Caption - Small Labels (12px/0.75rem) - Web Standard
export const AppCaption: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <span className={`text-grayUnit text-xs font-normal font-zain leading-tight ${className}`} {...props}>
    {children}
  </span>
);

// Status Text - For status badges (14px)
export const AppStatusText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <span className={`text-sm font-medium font-zain leading-tight ${className}`} {...props}>
    {children}
  </span>
);

// Warning Text (14px)
export const AppWarningText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <span className={`text-warning text-sm font-semibold font-zain leading-tight ${className}`} {...props}>
    {children}
  </span>
);

// Error Text (14px)
export const AppErrorText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <span className={`text-error text-sm font-semibold font-zain leading-tight ${className}`} {...props}>
    {children}
  </span>
);

// Success Text (14px)
export const AppSuccessText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <span className={`text-success text-sm font-semibold font-zain leading-tight ${className}`} {...props}>
    {children}
  </span>
);

// Table Text (14px)
export const AppTableText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <span className={`text-grayText text-sm font-medium font-zain leading-normal ${className}`} {...props}>
    {children}
  </span>
);

// Label Text (12px)
export const AppLabelText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <span className={`text-grayText text-xs font-semibold font-zain leading-tight ${className}`} {...props}>
    {children}
  </span>
);

// Button Text (14px)
export const AppButtonText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <span className={`text-sm font-medium font-zain leading-tight ${className}`} {...props}>
    {children}
  </span>
);

// Link Text (14px)
export const AppLinkText: React.FC<TypographyProps> = ({children, className = '', ...props}) => (
  <span className={`text-sm font-medium font-zain leading-tight underline ${className}`} {...props}>
    {children}
  </span>
);

// Default export for backward compatibility
export default {
  AppTitle,
  AppSubtitle,
  AppCardTitle,
  AppSectionTitle,
  AppText,
  AppBodyText,
  AppSmallText,
  AppCaption,
  AppStatusText,
  AppWarningText,
  AppErrorText,
  AppSuccessText,
  AppTableText,
  AppLabelText,
  AppButtonText,
  AppLinkText
};
